import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/PasarelaPago.css';

const CustomSelect = ({ options, value, onChange, name, placeholder, searchable }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const handleSelect = (val) => {
        onChange({ target: { name, value: val } });
        setIsOpen(false);
        setSearchTerm('');
    };

    const filteredOptions = searchable
        ? options.filter(opt => opt.label.toLowerCase().includes(searchTerm.toLowerCase()))
        : options;

    return (
        <div className="custom-select-container" onMouseLeave={() => { setIsOpen(false); setSearchTerm(''); }}>
            <div className="custom-select-trigger" onClick={() => setIsOpen(!isOpen)}>
                {options.find(opt => opt.value === value)?.label || value || placeholder}
                <span className={`arrow ${isOpen ? 'open' : ''}`}>▼</span>
            </div>
            {isOpen && (
                <div className="custom-select-options">
                    {searchable && (
                        <div className="select-search-container" onClick={(e) => e.stopPropagation()}>
                            <input
                                type="text"
                                placeholder="Buscar..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="select-search-input"
                                autoFocus
                            />
                        </div>
                    )}
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map(opt => (
                            <div
                                key={opt.value}
                                className={`custom-option ${opt.value === value ? 'selected' : ''}`}
                                onClick={() => handleSelect(opt.value)}
                            >
                                {opt.label}
                            </div>
                        ))
                    ) : (
                        <div className="custom-option no-results" style={{ padding: '10px', color: '#aaa', cursor: 'default' }}>
                            No se encontraron resultados
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const PasarelaPago = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    // Estado del formulario de datos personales
    const [formData, setFormData] = useState({
        nombre: '',
        apellidos: '',
        email: '',
        emailConfirm: '',
        prefijo: '+34',
        telefono: ''
    });

    // Estado de la tarjeta de crédito
    const [paymentData, setPaymentData] = useState({
        cardNumber: '',
        expiryMonth: '01',
        expiryYear: '2025',
        cvv: ''
    });

    const [errors, setErrors] = useState({});

    // Lista de prefijos
    const countryCodes = [
        { code: '+34', country: 'España' },
        { code: '+1', country: 'EE.UU. / Canadá' },
        { code: '+44', country: 'Reino Unido' },
        { code: '+33', country: 'Francia' },
        { code: '+49', country: 'Alemania' },
        { code: '+39', country: 'Italia' },
        { code: '+351', country: 'Portugal' },
        { code: '+52', country: 'México' },
        { code: '+54', country: 'Argentina' },
        { code: '+57', country: 'Colombia' },
        { code: '+56', country: 'Chile' },
        { code: '+51', country: 'Perú' },
        { code: '+55', country: 'Brasil' },
        { code: '+58', country: 'Venezuela' },
        { code: '+593', country: 'Ecuador' },
        { code: '+502', country: 'Guatemala' },
        { code: '+53', country: 'Cuba' },
        { code: '+591', country: 'Bolivia' },
        { code: '+504', country: 'Honduras' },
        { code: '+595', country: 'Paraguay' },
        { code: '+503', country: 'El Salvador' },
        { code: '+505', country: 'Nicaragua' },
        { code: '+506', country: 'Costa Rica' },
        { code: '+507', country: 'Panamá' },
        { code: '+598', country: 'Uruguay' },
        { code: '+31', country: 'Países Bajos' },
        { code: '+32', country: 'Bélgica' },
        { code: '+41', country: 'Suiza' },
        { code: '+46', country: 'Suecia' },
        { code: '+47', country: 'Noruega' },
        { code: '+45', country: 'Dinamarca' },
        { code: '+358', country: 'Finlandia' }
    ];
    // Opciones formateadas para el CustomSelect
    const countryOptions = countryCodes.map(c => ({ value: c.code, label: `${c.code} (${c.country})` }));

    // Formateador de teléfono (max 9 dígitos, de 3 en 3)
    const formatTelefono = (value) => {
        const cleaned = value.replace(/\D/g, '');
        const truncated = cleaned.slice(0, 9);
        const match = truncated.match(/.{1,3}/g);
        return match ? match.join(' ') : '';
    };

    // Formateador de tarjeta (max 16 dígitos, de 4 en 4)
    const formatCardNumber = (value) => {
        const cleaned = value.replace(/\D/g, '');
        const truncated = cleaned.slice(0, 16);
        const match = truncated.match(/.{1,4}/g);
        return match ? match.join(' ') : '';
    };

    // Manejo de cambios en datos personales
    const handleInputChange = (e) => {
        let value = e.target.value;
        if (e.target.name === 'telefono') {
            value = formatTelefono(value);
        }
        setFormData({
            ...formData,
            [e.target.name]: value
        });
    };

    // Manejo de cambios en pago
    const handlePaymentChange = (e) => {
        let value = e.target.value;
        if (e.target.name === 'cardNumber') {
            value = formatCardNumber(value);
        } else if (e.target.name === 'cvv') {
            value = value.replace(/\D/g, '').slice(0, 3);
        }
        setPaymentData({
            ...paymentData,
            [e.target.name]: value
        });
    };

    // Validación
    const validate = () => {
        let tempErrors = {};
        if (!formData.nombre) tempErrors.nombre = t("errorNombre");
        if (!formData.apellidos) tempErrors.apellidos = t("errorApellidos");
        
        if (!formData.email) {
            tempErrors.email = t("errorEmail");
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            tempErrors.email = t("errorEmailInvalid");
        }

        if (!formData.emailConfirm) {
            tempErrors.emailConfirm = t("errorEmailConfirm");
        } else if (formData.email !== formData.emailConfirm) {
            tempErrors.emailConfirm = t("errorEmailsMismatch");
        }

        // Validación de tarjeta (simple)
        if (!paymentData.cardNumber || !/^\d{16}$/.test(paymentData.cardNumber.replace(/\s/g, ''))) {
            tempErrors.cardNumber = t("errorCardInvalid");
        }
        if (!paymentData.cvv || !/^\d{3}$/.test(paymentData.cvv)) {
            tempErrors.cvv = t("errorCVVInvalid");
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            alert(t("paymentSuccess"));
            // Aquí iría la lógica real o redirección final
            navigate('/', {
              state: {
                ...location.state,
                openServicios: false // No queremos abrir servicios al terminar el pago
              }
            });
        }
    };

    // Generar datos para CustomSelects de fecha
    const currentYear = new Date().getFullYear();
    const yearsOptions = Array.from({ length: 21 }, (_, i) => {
        const y = (currentYear + i).toString();
        return { value: y, label: y };
    });

    const monthOptions = Array.from({ length: 12 }, (_, i) => {
        const m = (i + 1).toString().padStart(2, '0');
        return { value: m, label: m };
    });

    return (
        <div className="pasarela-page">
            <form onSubmit={handleSubmit} className="pasarela-form">
                <div className="pasarela-content">
                    <h2>{t('pasarelaTitle')}</h2>

                    {/* SECCIÓN 1: DATOS PERSONALES */}
                    <div className="form-section">
                        <h3>{t('personalDetails')}</h3>

                        <div className="form-group">
                            <label>{t('labelNombre')}</label>
                            <input
                                type="text"
                                name="nombre"
                                value={formData.nombre}
                                onChange={handleInputChange}
                                className={errors.nombre ? 'input-error' : ''}
                            />
                            {errors.nombre && <span className="error-msg">{errors.nombre}</span>}
                        </div>

                        <div className="form-group">
                            <label>{t('labelApellidos')}</label>
                            <input
                                type="text"
                                name="apellidos"
                                value={formData.apellidos}
                                onChange={handleInputChange}
                                className={errors.apellidos ? 'input-error' : ''}
                            />
                            {errors.apellidos && <span className="error-msg">{errors.apellidos}</span>}
                        </div>

                        <div className="form-group">
                            <label>{t('labelEmail')}</label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                onPaste={(e) => e.preventDefault()}
                                className={errors.email ? 'input-error' : ''}
                            />
                            {errors.email && <span className="error-msg">{errors.email}</span>}
                        </div>

                        <div className="form-group">
                            <label>{t('labelEmailConfirm')}</label>
                            <input
                                type="email"
                                name="emailConfirm"
                                value={formData.emailConfirm}
                                onChange={handleInputChange}
                                onPaste={(e) => e.preventDefault()}
                                className={errors.emailConfirm ? 'input-error' : ''}
                            />
                            {errors.emailConfirm && <span className="error-msg">{errors.emailConfirm}</span>}
                        </div>

                        <div className="form-group phone-group">
                            <label>{t('labelPhone')}</label>
                            <div className="phone-inputs">
                                <div style={{ minWidth: '140px' }}>
                                    <CustomSelect
                                        name="prefijo"
                                        value={formData.prefijo}
                                        onChange={handleInputChange}
                                        options={countryOptions}
                                        placeholder={t('placeholderPrefix')}
                                        searchable={true}
                                    />
                                </div>
                                <input
                                    type="tel"
                                    name="telefono"
                                    value={formData.telefono}
                                    onChange={handleInputChange}
                                    placeholder={t('placeholderPhone')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* SECCIÓN 2: DATOS DE PAGO */}
                    <div className="form-section payment-section">
                        <h3>{t('paymentInfo')}</h3>

                        <div className="credit-card-simulation">
                            <div className="form-group">
                                <label>{t('labelCardNumber')}</label>
                                <input
                                    type="text"
                                    name="cardNumber"
                                    value={paymentData.cardNumber}
                                    onChange={handlePaymentChange}
                                    placeholder={t('placeholderCard')}
                                    maxLength="19"
                                    className={errors.cardNumber ? 'input-error' : ''}
                                />
                                {errors.cardNumber && <span className="error-msg">{errors.cardNumber}</span>}
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('labelExpiry')}</label>
                                    <div className="expiry-inputs">
                                        <div style={{ flex: 1 }}>
                                            <CustomSelect
                                                name="expiryMonth"
                                                value={paymentData.expiryMonth}
                                                onChange={handlePaymentChange}
                                                options={monthOptions}
                                                placeholder="MM"
                                            />
                                        </div>
                                        <span>/</span>
                                        <div style={{ flex: 1.5 }}>
                                            <CustomSelect
                                                name="expiryYear"
                                                value={paymentData.expiryYear}
                                                onChange={handlePaymentChange}
                                                options={yearsOptions}
                                                placeholder="YYYY"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>{t('labelCVV')}</label>
                                    <input
                                        type="text"
                                        name="cvv"
                                        value={paymentData.cvv}
                                        onChange={handlePaymentChange}
                                        placeholder={t('placeholderCVV')}
                                        maxLength="3"
                                        className={errors.cvv ? 'input-error' : ''}
                                    />
                                    {errors.cvv && <span className="error-msg">{errors.cvv}</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="pasarela-footer">
                    <button type="button" className="cancel-button" onClick={() => navigate(-1)}>{t('cancelButton')}</button>
                    <button type="submit" className="pay-button">{t('payNowButton')}</button>
                </div>
            </form>
        </div>
    );
};

export default PasarelaPago;
